import React, { useEffect, useRef, useState } from "react";
import { Box, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MdOutlineKeyboardArrowLeft } from "react-icons/md";
import { AiOutlineWarning } from "react-icons/ai";
import Lottie from "lottie-react";

import {
  SIGNUP_COLORS,
  SIGNUP_LAYOUT,
  commonStyles,
} from "../../../pages/public/SignUp/tokens";
import { useAppState } from "../../../context/StateContext";
import logo from "../../../assets/img/logo.png";

import Button, { Color } from "../../../components/Form/Button";
import ProgressBar from "./ProgressBar";
import FooterBar from "./FooterBar";
import ConfirmationModal from "../../../components/Dialogs/ConfirmationModal";
import { UploadContentModal } from "../../../components/OnBoarding/UploadCenter/UploadContentModal";
import { ModalText, WarningText } from "../../../components/OnBoarding/ButtonsComponent/styles";

type SpacingX =
  | number
  | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };

export interface RightSideOnboardingProps {
  title?: string;
  subtitle?: string;
  showHeader?: boolean;
  children: React.ReactNode;
  showButtons?: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  nextDisabled?: boolean;
  loading?: boolean;
  showSkipButton?: boolean;
  showDownloadButton?: boolean;
  downloadUrl?: string;
  isFirstStep?: boolean;
  onExitOnboarding: () => void;
  progressValue: number;
  loadingUpdateDefault?: boolean;
  uploadType?: any;
  updateDefault?: (params: any) => void;
  contentScrollable?: boolean;
  contentMaxWidth?: number | "auto";
  headerIconLottie?: { animationData: any; size?: number };
  contentPaddingX?: SpacingX;
  contentBottomGutter?: number;
  buttonsMaxWidth?: number | "auto";
  stretchButtons?: boolean;
  backAsText?: boolean;
  autoFooterInset?: boolean;
  customClassName?: string;
  centerContent?: boolean;
}

const RightSideOnboarding: React.FC<RightSideOnboardingProps> = ({
  title,
  subtitle,
  showHeader = true,
  showButtons = true,
  children,

  onNext,
  onBack,
  onSkip,
  nextDisabled,
  loading,
  showSkipButton,
  showDownloadButton,
  downloadUrl,

  isFirstStep,
  onExitOnboarding,
  progressValue,

  loadingUpdateDefault,
  uploadType,
  updateDefault,

  contentScrollable,
  contentMaxWidth,
  headerIconLottie,
  contentBottomGutter,
  contentPaddingX,

  buttonsMaxWidth,
  stretchButtons,
  backAsText,

  autoFooterInset = true,
  customClassName,
  centerContent,
}) => {
  const { t: translate } = useTranslation();
  const { currentUser, userLogout } = useAppState();
  const [openExitModal, setOpenExitModal] = useState(false);

  const footerRef = useRef<HTMLDivElement | null>(null);
  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    if (!autoFooterInset || !showButtons) return;
    const node = footerRef.current;
    if (!node) return;

    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect?.height ?? 0;
      setFooterHeight(h);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [autoFooterInset, showButtons]);

  const scrollable =
    typeof contentScrollable === "boolean" ? contentScrollable : !isFirstStep;

  const maxW = isFirstStep
    ? SIGNUP_LAYOUT.maxWidthFirstStep
    : contentMaxWidth ?? SIGNUP_LAYOUT.maxWidthOtherSteps;

  const resolvedMaxWidth = maxW === "auto" ? "none" : (maxW as number | string);
  const resolvedMarginX = maxW === "auto" ? 0 : "auto";

  const resolvedPaddingX: SpacingX = contentPaddingX ?? { xs: 4, md: 6, lg: 8 };

  const resolvedButtonsMaxWidth =
    buttonsMaxWidth === "auto" ? "none" : buttonsMaxWidth ?? resolvedMaxWidth;
  const resolvedButtonsMx = buttonsMaxWidth === "auto" ? 0 : resolvedMarginX;

  const defaultGutter = isFirstStep ? 16 : 120;
  const manualGutter = contentBottomGutter ?? defaultGutter;

  return (
    <Box
      className={customClassName}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: SIGNUP_COLORS.white,
        position: "relative",
      }}
    >
      {/* Barra de progresso */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 10, bgcolor: SIGNUP_COLORS.white }}>
        <ProgressBar value={progressValue} />
      </Box>

      {/* Small logo visible only on xs screens (phones) */}
        <Box
          component="img"
          src={logo}
          alt="Logo"
          sx={{
            display: { xs: "flex", md: "none" },
            maxWidth: "120px",
            height: "auto",
            pt: "24px",
            pl: "24px",
          }}
        />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: (isFirstStep || centerContent) ? "center" : "flex-start",
          pb: isFirstStep ? 2 : { xs: 14, lg: 18 },
          pt: isFirstStep ? { xs: 2, md: 4 } : "36px",
          px: resolvedPaddingX,
          minHeight: 0,
          overflowY: scrollable ? "auto" : "visible",
          width: "100%",
          maxWidth: resolvedMaxWidth,
          mx: resolvedMarginX,
          position: "relative",
          zIndex: 1,
        }}
      >
        {showHeader && title && (
          <Stack spacing={2} mb={4} position="relative">
            {headerIconLottie?.animationData && (
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  opacity: 0.95,
                  pointerEvents: "none",
                }}
              >
                <Lottie
                  animationData={headerIconLottie.animationData}
                  loop
                  autoplay
                  style={{
                    width: headerIconLottie.size ?? 56,
                    height: headerIconLottie.size ?? 56,
                  }}
                />
              </Box>
            )}

            <Box
              component="h1"
              sx={{
                fontFamily: "Manrope",
                fontWeight: 600,
                fontSize: "28px",
                lineHeight: "36px",
                letterSpacing: "-0.02em",
                color: SIGNUP_COLORS.textPrimary,
                m: 0,
              }}
            >
              {title}
            </Box>
            {subtitle && (
              <Box
                component="p"
                sx={{
                  fontFamily: "Manrope",
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "24px",
                  color: SIGNUP_COLORS.gray550,
                  m: 0,
                }}
              >
                {subtitle}
              </Box>
            )}
          </Stack>
        )}

        <Box
        sx={isFirstStep ? { height: "100%", overflowY: "auto" } : undefined}
        >{children}</Box>

        {!isFirstStep && (
          <Box sx={{ height: (autoFooterInset ? footerHeight : 0) + (manualGutter || 0) }} />
        )}
      </Box>

      {showButtons && (
        <Box
          ref={footerRef}
          sx={{
            px: resolvedPaddingX,
            py: 1,
            bgcolor: SIGNUP_COLORS.white,
            position: "relative",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: resolvedButtonsMaxWidth,
              mx: resolvedButtonsMx,
            }}
          >
            {stretchButtons ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  columnGap: 2,
                }}
              >
                {isFirstStep ? (
                  <Button
                    disabled={loading}
                    onClick={() => setOpenExitModal(true)}
                    customColor={Color.INFO}
                    data-cy="exitOnboarding"
                    sx={{ ...commonStyles.backButton, height: 40 }}
                  >
                    {translate("onboarding.buttons.goBackToLogin")}
                  </Button>
                ) : (
                  <Button
                    disabled={loading}
                    onClick={onBack}
                    customColor={Color.INFO}
                    data-cy="backButton"
                    sx={{ ...commonStyles.backButton, height: 40 }}
                  >
                    {translate("onboarding.buttons.back")}
                  </Button>
                )}

                <Stack direction="row" spacing={2} alignItems="center">
                  {showSkipButton && (
                    <Button
                      disabled={loading}
                      onClick={onSkip}
                      customColor={Color.INFO}
                      data-cy="skipButton"
                      sx={{ ...commonStyles.backButton, height: 40 }}
                    >
                      {translate("onboarding.buttons.skip")}
                    </Button>
                  )}

                  {showDownloadButton && downloadUrl && (
                    <a href={downloadUrl} download style={{ textDecoration: "none" }}>
                      <Button
                        disabled={loading}
                        customColor={Color.INFO}
                        data-cy="downloadButton"
                        sx={{ ...commonStyles.backButton, height: 40 }}
                      >
                        {translate("onboarding.buttons.downloadCsvTemplate")}
                      </Button>
                    </a>
                  )}

                  <Box sx={{ flex: 1 }}>
                    <Button
                      disabled={nextDisabled || loading}
                      onClick={onNext}
                      customColor={Color.SECONDARY}
                      data-cy="nextButton"
                      loading={loading}
                      sx={{ ...commonStyles.primaryButton, height: 40, width: "100%" }}
                    >
                      {translate("onboarding.buttons.next")}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ) : (
              <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                {isFirstStep ? (
                  <Button
                    disabled={loading}
                    onClick={() => setOpenExitModal(true)}
                    customColor={Color.INFO}
                    data-cy="exitOnboarding"
                    sx={{ ...commonStyles.backButton, width: 250, height: 40 }}
                  >
                    {translate("onboarding.buttons.goBackToLogin")}
                  </Button>
                ) : backAsText ? (
                  <Button
                    disabled={loading}
                    onClick={onBack}
                    customColor={Color.INFO}
                    data-cy="backButton"
                    sx={{ ...commonStyles.backButton, width: 250, height: 40 }}
                  >
                    {translate("onboarding.buttons.back")}
                  </Button>
                ) : (
                  <Button
                    disabled={loading}
                    onClick={onBack}
                    customColor={Color.SEND}
                    data-cy="backButton"
                    sx={{ minWidth: 40, height: 40, borderRadius: SIGNUP_LAYOUT.borderRadius }}
                  >
                    <MdOutlineKeyboardArrowLeft size={20} />
                  </Button>
                )}

                <Stack direction="row" spacing={2} alignItems="center">
                  {showSkipButton && (
                    <Button
                      disabled={loading}
                      onClick={onSkip}
                      customColor={Color.INFO}
                      data-cy="skipButton"
                      sx={{ ...commonStyles.backButton, width: 250, height: 40 }}
                    >
                      {translate("onboarding.buttons.skip")}
                    </Button>
                  )}

                  {showDownloadButton && downloadUrl && (
                    <a href={downloadUrl} download style={{ textDecoration: "none" }}>
                      <Button
                        disabled={loading}
                        customColor={Color.INFO}
                        data-cy="downloadButton"
                        sx={{ ...commonStyles.backButton, width: 250, height: 40 }}
                      >
                        {translate("onboarding.buttons.downloadCsvTemplate")}
                      </Button>
                    </a>
                  )}

                  <Button
                    disabled={nextDisabled || loading}
                    onClick={onNext}
                    customColor={Color.SECONDARY}
                    data-cy="nextButton"
                    loading={loading}
                    sx={{ ...commonStyles.primaryButton, width: 250, height: 40 }}
                  >
                    {translate("onboarding.buttons.next")}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>
        </Box>
      )}

      <FooterBar />

      <UploadContentModal
        isOpen={!!loadingUpdateDefault}
        showWarning
        onClickTryAgain={() =>
          updateDefault?.({
            companyId: currentUser?.companyId,
            uploadType,
            branchId:
              currentUser?.branches && currentUser?.branches?.length
                ? currentUser?.branches[0].id
                : undefined,
          })
        }
      />

      <ConfirmationModal
        open={openExitModal}
        title={translate("leavingOnboarding")}
        type="warning"
        leftIconTitle={<AiOutlineWarning color="#F79009" size={20} />}
        closeModal={() => setOpenExitModal(false)}
        submit={userLogout}
        textSubmit={translate("leave")}
        customColorSave={Color.WARNING}
        customColorCancel={Color.TEXT_INFO}
      >
        <ModalText>
          {translate("youAreAboutToExitTheOnboardingProcessAreYouSureYouWantToDoThis")}
        </ModalText>
        <WarningText>
          {translate("rememberThatYouWillOnlyBeAbleToUseTheSystemAfterCompletingThisProcess")}
        </WarningText>
      </ConfirmationModal>
    </Box>
  );
};

export default RightSideOnboarding;
